import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandlerFn,
    HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { AuthState } from 'app/core/state/auth.state';
import { Observable, catchError, from, switchMap, throwError } from 'rxjs';
import { environment } from 'environments/environment';

// Tracks whether a refresh is already in flight to avoid parallel refreshes
let refreshInProgress: Promise<void> | null = null;

/**
 * Auth Interceptor for Custom JWT
 *
 * - Skips auth for static assets and /auth/exchange
 * - Adds custom JWT to API requests
 * - On 401: silently refreshes token via MSAL and retries the request once
 */
export const authInterceptor = (
    req: HttpRequest<unknown>,
    next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
    const authState = inject(AuthState);
    const authService = inject(AuthService);
    const router = inject(Router);

    // URLs that don't need any token
    const skipUrls = ['/assets/', '/i18n/'];

    if (skipUrls.some(url => req.url.includes(url))) {
        return next(req);
    }

    // Skip interceptor for token exchange (uses Microsoft token directly)
    if (req.url.includes('/auth/exchange')) {
        return next(req);
    }

    // Check if request is to our API
    const isApiRequest = req.url.includes(environment.apiUrl) ||
                         req.url.includes('localhost:7284');

    if (!isApiRequest) {
        return next(req);
    }

    // Attach token and send request
    const sendRequest = (): Observable<HttpEvent<unknown>> => {
        let outgoing = req;

        // Don't overwrite if Authorization already set (e.g. auth.service.ts token exchange)
        if (!req.headers.has('Authorization')) {
            const token = authState.accessToken();
            if (token) {
                let headers = req.headers.set('Authorization', `Bearer ${token}`);

                // If admin has selected a context, send it as override header
                // so the backend uses the admin-selected location instead of the JWT default
                const adminCtx = authState.adminContext();
                if (adminCtx?.orgRegionLocationId) {
                    headers = headers.set('X-Admin-Location-Id', adminCtx.orgRegionLocationId);
                }
                if (adminCtx?.ownerOrganizationId) {
                    headers = headers.set('X-Admin-Org-Id', adminCtx.ownerOrganizationId);
                }
                if (adminCtx?.orgRegionId) {
                    headers = headers.set('X-Admin-Region-Id', adminCtx.orgRegionId);
                }

                outgoing = req.clone({ headers });
            }
        }

        return next(outgoing);
    };

    // Handle errors — retry once on 401 after refreshing token
    const handleErrorWithRetry = (error: HttpErrorResponse, isRetry: boolean): Observable<HttpEvent<unknown>> => {
        if (error.status === 401 && !isRetry) {
            // Token expired — try to silently refresh and retry
            console.warn('401 Unauthorized — attempting silent token refresh');

            // Coalesce concurrent refreshes into one call
            if (!refreshInProgress) {
                refreshInProgress = authService.silentRefresh().finally(() => {
                    refreshInProgress = null;
                });
            }

            return from(refreshInProgress).pipe(
                switchMap(() => {
                    // Retry the original request with the new token
                    const newToken = authState.accessToken();
                    const retryReq = req.clone({
                        headers: req.headers.set('Authorization', `Bearer ${newToken}`)
                    });
                    return next(retryReq).pipe(
                        catchError((retryError: HttpErrorResponse) => handleErrorWithRetry(retryError, true))
                    );
                }),
                catchError(() => {
                    // Refresh failed — give up, clear auth, redirect
                    console.warn('Silent token refresh failed — redirecting to sign-in');
                    authState.clearAuth();
                    router.navigate(['/sign-in']);
                    return throwError(() => error);
                })
            );
        }

        if (error.status === 401 && isRetry) {
            // Already retried once — truly unauthorized
            console.warn('401 after token refresh — clearing auth state');
            authState.clearAuth();
            router.navigate(['/sign-in']);
        }

        if (error.status === 403) {
            console.warn('403 Forbidden:', error.message);
        }

        return throwError(() => error);
    };

    return sendRequest().pipe(
        catchError((error: HttpErrorResponse) => handleErrorWithRetry(error, false))
    );
};